import React from "react";
import { Grid } from "@material-ui/core";
import { TextField } from "@ofqual/portal-components/components/TextField";
import { Controller } from "react-hook-form";
import { EventNotification } from "../../domain/EventNotification";
import { SectionProps } from "./SectionProps";
import { Select } from "@ofqual/portal-components/components/Select";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import { whoAwareService } from "../../services";
import { DatePicker } from "@ofqual/portal-components/components/DatePicker";
import { fieldLabels } from "./fieldLabels";
import { isFutureDate } from "@ofqual/portal-core/core/utils/dateUtils";
import { Error } from "../common/Error";

export const Awareness: React.FC<SectionProps<EventNotification>> = ({
  control,
  errors,
  disabled,
  lookupTracker,
  formState,
}) => {
  const [whoAwareLookups] = useLookup(whoAwareService);

  const { dirtyFields } = formState;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="howBecameAware"
          as={
            <TextField
              variant="outlined"
              label={fieldLabels["howBecameAware"]}
              fullWidth
              readOnly={disabled}
              multiline
              data-test="how-became-aware"
            />
          }
        />
      </Grid>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="natureAndCause"
          as={
            <TextField
              variant="outlined"
              label={fieldLabels["natureAndCause"]}
              fullWidth
              readOnly={disabled}
              multiline
              data-test="nature-and-cause"
            />
          }
        />
      </Grid>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="adverseEffect"
          as={
            <Select
              label={fieldLabels["adverseEffect"]}
              readOnly={disabled}
              datatest="adverse-effect"
              lookupTracker={lookupTracker}
            >
              {[
                { id: 1, label: "Potential" },
                { id: 2, label: "Actual" },
              ]}
            </Select>
          }
        />
      </Grid>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="potentialOrActualAdverseEffect"
          as={
            <TextField
              variant="outlined"
              label={fieldLabels["potentialOrActualAdverseEffect"]}
              fullWidth
              multiline
              readOnly={disabled}
              data-test="adverse-effect-description"
            />
          }
        />
      </Grid>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="eventNotificationWhoIsAware"
          as={
            <Select
              label={fieldLabels["eventNotificationWhoIsAware"]}
              readOnly={disabled}
              selectVariant="multiple"
              datatest="who-is-aware"
              lookupTracker={lookupTracker}
            >
              {whoAwareLookups}
            </Select>
          }
        />
      </Grid>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="whoIsAwareDetails"
          as={
            <TextField
              variant="outlined"
              label={fieldLabels["whoIsAwareDetails"]}
              fullWidth
              readOnly={disabled}
              multiline
              data-test="who-is-aware-details"
            />
          }
        />
      </Grid>

      <Grid item xs={12} sm={8}>
        <Controller
          rules={{
            validate: (value) =>
              dirtyFields.dateOfNextUpdate === true
                ? isFutureDate(value)
                  ? true
                  : false
                : true,
          }}
          control={control}
          name="dateOfNextUpdate"
          as={
            <DatePicker
              returnDayBoundary="endOfDay"
              label={fieldLabels["dateOfNextUpdate"]}
              readOnly={disabled}
              dataTest="date-of-next-update"
            />
          }
        />
        {errors.dateOfNextUpdate?.type === "validate" && (
          <Error>
            Date of next update must be after or the same as today's date
          </Error>
        )}
      </Grid>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="additionalInformation"
          as={
            <TextField
              variant="outlined"
              label={fieldLabels["additionalInformation"]}
              fullWidth
              readOnly={disabled}
              data-test="additional-information"
              multiline
            />
          }
        />
      </Grid>
    </Grid>
  );
};
